<template>
  <div class="memo">
    <div id="memo" class="col memo-container">
      <div class="memo-container-create">
        <div class="line">
          <div class="filters enabled">
            <button class="filter-btn bold" @click.stop="execCommand('bold')">B</button>
            <button class="filter-btn italic" @click.stop="execCommand('italic')">I</button>
            <button class="filter-btn underline" @click.stop="execCommand('underline')">U</button>
          </div>
        </div>
        <div
          ref="contentEditableCreate"
          class="memo-content-create"
          :class="{
            placeholder: contentModel === $t('clickToCreateMemo'),
          }"
          contenteditable
          @click="reset"
          @input="update"
        ></div>
        <div class="memo-content-footer">
          <div v-if="!validate" class="info">
            <div class="icon-warning" />
            {{ $t('maximumCharacter') }}
          </div>
          <div v-if="!loading" class="main-button" :class="{ disable: !validate }" @click.stop="send">
            {{ $t('add') }}
          </div>
          <div v-else class="main-button">
            <div class="lds-ellipsis">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
        </div>
      </div>
      <div class="container">
        <div class="memo-container">
          <div v-for="memo in tableValues" :key="memo.id" class="memo-content" :class="{ edit: focus === memo.id }">
            <div class="line">
              <div class="date">{{ $utils.formatDate(memo.createdAt) }}</div>
            </div>
            <div class="line">
              <div v-if="memo.user" class="name">{{ memo.user.firstName }} {{ memo.user.lastName }}</div>
              <div class="filters" :class="{ enabled: focus === memo.id }">
                <button class="filter-btn bold" @click.stop="execCommand('bold', memo.id)">B</button>
                <button class="filter-btn italic" @click.stop="execCommand('italic', memo.id)">I</button>
                <button class="filter-btn underline" @click.stop="execCommand('underline', memo.id)">U</button>
              </div>
            </div>
            <div :ref="memo.id" class="content" v-html="memo.memo"></div>
            <IconEdit v-if="!focus && !loading" class="icon icon-edit" :class="{ disable: !validate }" @click.stop="() => edit(memo.id)" />
            <IconSave v-if="!loading && focus === memo.id" class="icon icon-edit" :class="{ disable: !validate }" @click.prevent="save(memo.id)" />
          </div>
        </div>
        <Pagination class="pagination" :pagination="pagination" :set-page="setPage" :tableValues="tableValues" :pages="pages" :disable-dots="disableDots" />
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination';
import tableMixin from '@/components/tables/TableMixin';

export default {
  components: { Pagination },
  mixins: [tableMixin],

  beforeRouteLeave(to, from, next) {
    if (this.isSend) return next();
    if (this.contentModel.length && this.contentModel !== '' && this.contentModel !== this.$t('clickToCreateMemo') && confirm(this.$t('areYouSureToExit'))) {
      return next();
    } else if (this.$refs[this.focus] && this.$refs[this.focus][0] && confirm(this.$t('areYouSureToExit'))) {
      return next();
    } else if (
      !(this.contentModel.length && this.contentModel !== '' && this.contentModel !== this.$t('clickToCreateMemo')) &&
      !(this.$refs[this.focus] && this.$refs[this.focus][0])
    ) {
      return next();
    }
  },

  props: {
    company: {
      type: Object,
      required: true,
    },

    isMonitored: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      contentModel: this.$t('clickToCreateMemo'),
      tableMixinConfig: {
        backendPaginated: true,
        onChange: this.changePage,
        getData: this.getData,
        maxElement: 3,
      },
      values: [],
      focus: '',
    };
  },

  computed: {
    validate() {
      return this.contentModel.length <= 4000;
    },
  },

  created() {
    if (!this.isMonitored) {
      this.$router.push({ name: 'searchByIdEnterprise', replace: true });
    }
  },

  async mounted() {
    this.$refs['contentEditableCreate'].innerHTML = this.contentModel;
    this.$refs['contentEditableCreate'].addEventListener('paste', function (e) {
      e.preventDefault();
      var text = (e.originalEvent || e).clipboardData.getData('text/plain');
      document.execCommand('insertHTML', false, text);
    });
  },

  methods: {
    execCommand(command, id) {
      document.execCommand(command, false, null);
      if (id) this.edit(id);
      else this.$refs['contentEditableCreate'].focus();
    },

    async getData(pageNumber = 0, pageSize = this.tableMixinConfig.maxElement) {
      let data = await this.$store.dispatch('getCompanyMemos', {
        searchedCompanySiren: this.company.siren,
        pageNumber,
        pageSize,
      });
      this.values = data.items;
      this.pagination.totalLength = data.count;
      this.pagination.maxPage = Math.ceil(this.pagination.totalLength / this.pagination.maxElement);
      return { data, values: data.items };
    },

    update(event) {
      this.isSend = true;
      this.contentModel = event.target.innerHTML;
      if (this.contentModel.length && this.contentModel !== '' && this.contentModel !== this.$t('clickToCreateMemo')) {
        window.onbeforeunload = () => confirm(this.$t('areYouSureToExit'));
      } else window.onbeforeunload = null;
    },

    async send() {
      this.isSend = true;

      if (this.validate && this.contentModel.length >= 1 && this.contentModel !== this.$t('clickToCreateMemo')) {
        await this.$store.dispatch('addCompanyMemo', {
          searchedCompanySiren: this.company.siren,
          memo: this.contentModel,
        });
        this.contentModel = this.$t('clickToCreateMemo');
        this.$refs['contentEditableCreate'].innerHTML = this.contentModel;
        this.setPage(0);
      }
    },

    redoPlaceholder() {
      if (this.contentModel === '') {
        this.contentModel = this.$t('clickToCreateMemo');
        this.$refs['contentEditableCreate'].innerHTML = this.contentModel;
      }
    },

    reset() {
      if (this.contentModel === this.$t('clickToCreateMemo')) {
        this.contentModel = '';
        this.$refs['contentEditableCreate'].innerHTML = this.contentModel;
      }
    },

    edit(id) {
      this.$refs[id][0].contentEditable = true;
      this.focus = id;
      this.$refs[id][0].focus();

      this.tableValues.forEach(e => {
        this.$refs[e.id][0].addEventListener('paste', function (e) {
          e.preventDefault();
          var text = (e.originalEvent || e).clipboardData.getData('text/plain');
          document.execCommand('insertHTML', false, text);
        });
      });
    },

    async save(id) {
      this.$refs[id][0].contentEditable = false;
      await this.$store.dispatch('updateCompanyMemo', {
        id,
        searchedCompanySiren: this.company.siren,
        memo: this.$refs[id][0].innerHTML,
      });
      this.focus = '';
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.memo {
  width: 100%;
  display: flex;
  padding: 80px 0px 80px;
  box-sizing: border-box;

  .col {
    &.memo-container {
      display: flex;
      flex-grow: 1;
      margin-bottom: 135px;

      .title {
        color: colors.$darkblue-color;
        font-weight: 500;
        font-size: 20px;
        margin: 0 0 24px;

        &:not(:first-child) {
          padding-top: 80px;
        }
      }

      .line {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .date {
          font-size: 13px;
          line-height: 15px;
          color: colors.$grey-color;
          margin: 0 0 8px;
        }

        .name {
          flex-grow: 1;
          font-weight: 500;
          font-size: 16px;
          color: colors.$black-color;
          margin: 0 0 20px;
        }

        .filters {
          align-self: flex-end;
          flex-grow: 1;
          display: flex;
          align-items: flex-end;
          justify-content: flex-end;
          height: 0;
          overflow: hidden;
          transition: all 0.3s;
          opacity: 0;
          &.enabled {
            height: 30px;
            opacity: 1;
          }

          .filter-btn {
            width: 36px;
            box-sizing: border-box;
            border: 1px solid colors.$lightgrey-color;
            margin-bottom: 6px;
            padding: 4px;
            border-radius: 4px;
            cursor: pointer;

            &.bold {
              font-weight: bold;
            }

            &.italic {
              font-style: italic;
            }

            &.underline {
              text-decoration: underline;
            }

            &:hover {
              background: colors.$lightgrey-color;
              border-color: colors.$grey-color;
            }

            &:not(:first-child) {
              border-left: 0;
            }

            &:nth-child(2n) {
              border-radius: 0;
            }

            &:first-child {
              border-top-right-radius: 0px;
              border-bottom-right-radius: 0px;
            }

            &:last-child {
              border-top-left-radius: 0px;
              border-bottom-left-radius: 0px;
            }
          }
        }
      }

      .memo-container-create {
        display: flex;
        align-items: flex-end;
        flex-direction: column;
        width: 480px;
        height: 480px;

        .memo-content-create {
          flex-grow: 1;
          box-sizing: border-box;
          width: 100%;
          height: 100%;
          border: 1px solid colors.$lightgrey-color;
          border-radius: 5px;
          padding: 30px;
          transition: all 0.3s;
          overflow-y: auto;

          &:focus {
            border-radius: 5px !important;
            border: 1px solid colors.$lightblue-color;
            outline: none;
          }

          &.placeholder {
            color: colors.$grey-color;
          }
        }
        .memo-content-footer {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 20px;

          .info {
            height: 100%;
            color: colors.$red-color;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
          }

          .main-button {
            max-width: 160px;
            cursor: pointer;

            &.disable {
              cursor: not-allowed;
              background: colors.$lightgrey-color;
            }
          }
        }
      }
      .container {
        max-width: 60%;
        flex-grow: 1;
        display: flex;
        flex-direction: column;
      }
      .memo-container {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;

        .memo-content {
          box-sizing: border-box;
          background: rgba(colors.$lightgrey-color, 0.2);
          flex-grow: 1;
          min-height: 200px;
          margin: 22px 0px 22px 22px;
          padding: 30px 30px 70px;
          border-radius: 5px;
          transition: all 0.3s;
          border: 1px solid transparent;
          position: relative;
          border-radius: 4px;

          &:first-child {
            margin-top: 0;
          }

          &:last-child {
            margin-bottom: 0;
          }

          .content {
            box-sizing: border-box;
            font-size: 14px;
            color: colors.$darkgrey-color;
            white-space: pre-wrap;
            padding: 8px;
            outline: none;
            border: 1px solid transparent;
            word-break: break-all;
          }

          &.edit {
            border: 1px solid colors.$lightgrey-color;
            .content {
              background: colors.$white-color;
              border: 1px solid colors.$lightgrey-color;
            }
          }

          .icon {
            position: absolute;
            flex-shrink: 0;
            right: 30px;
            bottom: 30px;
            min-width: 0;
            margin: 0;
            margin-left: calc(100% - 48px);
            width: 17px;
            height: 17px;
            padding: 6px;
            background: rgba(colors.$lightgrey-color, 0.3);
            border-radius: 50%;
            transition: all 0.3s;
            cursor: pointer;
            color: colors.$grey-color;

            &:hover {
              color: #fff;
              background: colors.$lightblue-color;
            }
          }
        }
      }
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-small) {
  .memo-container-create {
    width: 100% !important;
  }

  .memo {
    padding-bottom: 0;
  }

  .memo-container {
    flex-direction: column;
    margin-bottom: 0 !important;
  }

  .container {
    max-width: unset !important;

    .memo-content {
      margin: 22px 0 !important;
    }
  }

  .memo-container-create {
    margin-bottom: 26px;
    height: 324px !important;
  }
}
</style>
